import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Tag,
  Button,
  Statistic,
  Row,
  Col,
  Avatar,
  Spin,
  Empty,
  Divider,
  message,
  Table
} from 'antd';
import {
  TrophyOutlined,
  VideoCameraOutlined,
  StarOutlined,
  ArrowLeftOutlined,
  YoutubeOutlined,
  CalendarOutlined,
  UserOutlined,
  GoogleOutlined,
  HomeOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { apiClient } from '../services/api';
import './DetailHistory.css';

const AffiliateDetail = () => {
  const { affiliateId } = useParams();
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAffiliateDetail = async () => {
      setLoading(true);
      try {
        // Fetch affiliate details and contributions from backend
        const response = await apiClient.get(`/api/affiliates/${affiliateId}/detail`);

        if (response.data.status === 'success') {
          setAffiliate(response.data.data.affiliate);
          setContributions(response.data.data.contributions || []);
        } else {
          message.error('Gagal memuat detail affiliate');
        }
      } catch (error) {
        console.error('Error loading affiliate details:', error);
        message.error('Gagal memuat data dari server');
      } finally {
        setLoading(false);
      }
    };

    loadAffiliateDetail();
  }, [affiliateId]);

  const getRankColor = (rank) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return 'blue';
  };

  const getRankIcon = (rank) => {
    if (rank <= 3) return <TrophyOutlined />;
    return <StarOutlined />;
  };

  const getRoleLabel = (type) => {
    const labels = {
      'mahasiswa': 'Mahasiswa',
      'dosen': 'Dosen',
      'tenaga_pendidik': 'Tenaga Pendidik'
    };
    return labels[type] || type;
  };

  const getRoleColor = (type) => {
    const colors = {
      'mahasiswa': 'blue',
      'dosen': 'green',
      'tenaga_pendidik': 'purple'
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <div className="history-loading">
        <Spin size="large" tip="Memuat detail affiliate..." />
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="history-container">
        <div className="history-content">
          <Card className="not-found-card">
            <Empty
              description="Affiliate tidak ditemukan"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
              style={{ marginTop: 20 }}
            >
              Kembali ke Leaderboard
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: 'Tipe',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => {
        if (type === 'certification') {
          return <Tag icon={<SafetyCertificateOutlined />} color="orange">Sertifikat</Tag>;
        }
        return <Tag icon={<VideoCameraOutlined />} color="blue">Video</Tag>;
      },
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform, record) => {
        if (record.type === 'certification') {
          return <Tag icon={<SafetyCertificateOutlined />} color="orange">Certification</Tag>;
        }
        const icon = platform === 'youtube' ?
          <YoutubeOutlined style={{ color: '#FF0000' }} /> :
          <GoogleOutlined style={{ color: '#4285F4' }} />;
        return <Tag icon={icon}>{platform}</Tag>;
      },
    },
    {
      title: 'Konten',
      dataIndex: 'content_text',
      key: 'content_text',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.type === 'certification' && record.certification_type && (
            <Tag color="purple" style={{ marginTop: 4 }}>{record.certification_type}</Tag>
          )}
          {record.hashtag && <Tag color="blue" style={{ marginTop: 4 }}>{record.hashtag}</Tag>}
        </div>
      ),
    },
    {
      title: 'Tanggal',
      dataIndex: 'posted_at',
      key: 'posted_at',
      render: (date, record) => {
        const label = record.type === 'certification' ? 'Selesai: ' : '';
        return date ? label + new Date(date).toLocaleDateString('id-ID') : '-';
      },
    },
    {
      title: 'Poin',
      dataIndex: 'score',
      key: 'score',
      render: (score) => <Tag color="green">+{score || 1}</Tag>,
    },
    {
      title: 'Aksi',
      key: 'action',
      render: (_, record) => {
        if (record.type === 'certification') {
          // For certifications, show view certificate button if file exists
          if (record.content_url) {
            return (
              <Button
                type="link"
                icon={<SafetyCertificateOutlined />}
                onClick={() => window.open(record.content_url, '_blank')}
              >
                Lihat Sertifikat
              </Button>
            );
          }
          return <span style={{ color: '#999' }}>-</span>;
        }
        // For videos, show view video button
        return (
          <Button
            type="link"
            icon={<YoutubeOutlined />}
            onClick={() => window.open(record.content_url, '_blank')}
          >
            Lihat Video
          </Button>
        );
      },
    },
  ];

  return (
    <div className="history-container">
      <div className="history-header">
        <Button
          icon={<HomeOutlined />}
          onClick={() => navigate('/')}
          className="back-button"
        >
          Kembali ke Leaderboard
        </Button>
      </div>

      <div className="history-content">
        {/* Affiliate Profile Card */}
        <Card className="profile-card">
          <div className="profile-header">
            <Avatar
              size={100}
              src={affiliate.picture}
              icon={<UserOutlined />}
              className="profile-avatar"
            />
            <div className="profile-info">
              <h1>{affiliate.name}</h1>
              <p className="nim-nip">{affiliate.external_id}</p>
              <div style={{ marginTop: 8 }}>
                <Tag
                  color={getRoleColor(affiliate.type)}
                  className="role-tag"
                  style={{ fontSize: '14px', padding: '4px 12px' }}
                >
                  {getRoleLabel(affiliate.type)}
                </Tag>
                {affiliate.unit_name && (
                  <Tag color="cyan" style={{ fontSize: '14px', padding: '4px 12px' }}>
                    {affiliate.unit_code} - {affiliate.unit_name}
                  </Tag>
                )}
              </div>
              {affiliate.email && (
                <p style={{ marginTop: 8, color: '#666' }}>{affiliate.email}</p>
              )}
            </div>
            {affiliate.rank && (
              <div className="rank-badge" style={{ borderColor: getRankColor(affiliate.rank) }}>
                {getRankIcon(affiliate.rank)}
                <span className="rank-text">Rank #{affiliate.rank}</span>
              </div>
            )}
          </div>

          <Divider />

          <Row gutter={[16, 16]} className="stats-row">
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total Poin"
                value={affiliate.total_score || 0}
                prefix={<StarOutlined />}
                valueStyle={{
                  color: '#14B8A6',
                  fontWeight: 700
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total Video"
                value={affiliate.total_verifications || 0}
                prefix={<VideoCameraOutlined />}
                valueStyle={{
                  color: '#14B8A6',
                  fontWeight: 700
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total Sertifikat"
                value={affiliate.total_certifications || 0}
                prefix={<SafetyCertificateOutlined />}
                valueStyle={{
                  color: '#FF8E53',
                  fontWeight: 700
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Rata-rata Poin"
                value={affiliate.total_verifications > 0 ?
                  (affiliate.total_score / affiliate.total_verifications).toFixed(1) : 0}
                suffix="/ konten"
                valueStyle={{
                  color: '#FFD700',
                  fontWeight: 700
                }}
              />
            </Col>
          </Row>
        </Card>

        {/* Contributions Table Card */}
        <Card
          className="history-card"
          title={
            <div className="card-title">
              <CalendarOutlined />
              <span>Riwayat Kontribusi (Video & Sertifikat)</span>
            </div>
          }
        >
          {contributions.length === 0 ? (
            <Empty
              description="Belum ada riwayat kontribusi"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={contributions}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} kontribusi (video & sertifikat)`,
              }}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default AffiliateDetail;
